/**
 * Stable IDs for every border element in the get-in-touch contact overlay.
 * Use these names when requesting layout, animation, or style updates.
 *
 * Naming pattern: `{column}.{element}`
 * - column: `detailsColumn` | `formColumn`
 * - element: role of the border (bleed, edge, corner, divider)
 */
export const getInTouchOverlayBorderNames = {
  /** Infinite top rule — extends left from the details column top edge. */
  detailsColumnTopBleedLeft: 'overlay.details-column.top-bleed-left',
  /** In-cell top rule — from column left edge to the corner box. */
  detailsColumnTopEdge: 'overlay.details-column.top-edge',
  /** Top-right squircle corner box on the details column. */
  detailsColumnTopRightCorner: 'overlay.details-column.top-right-corner',
  /** Vertical column divider between the details top and form bottom corner boxes. */
  detailsColumnColumnDivider: 'overlay.details-column.column-divider',
  /** In-cell bottom rule — from the corner box to the column right edge. */
  formColumnBottomEdge: 'overlay.form-column.bottom-edge',
  /** Bottom-left squircle corner box on the form column. */
  formColumnBottomLeftCorner: 'overlay.form-column.bottom-left-corner',
  /** Infinite bottom rule — extends right from the form column bottom edge. */
  formColumnBottomBleedRight: 'overlay.form-column.bottom-bleed-right',
} as const

export type GetInTouchOverlayBorderName =
  (typeof getInTouchOverlayBorderNames)[keyof typeof getInTouchOverlayBorderNames]

type BorderDrawStep = {
  drawDelayMs: number
  drawDurationMs: number
  drawDirection?: 'ltr' | 'rtl'
}

/** Chain draw steps so each begins when the previous finishes. */
function chainBorderDraw(steps: Omit<BorderDrawStep, 'drawDelayMs'>[]): BorderDrawStep[] {
  let delayMs = 0
  return steps.map((step) => {
    const draw = { drawDelayMs: delayMs, ...step }
    delayMs += step.drawDurationMs
    return draw
  })
}

const [
  detailsColumnTopBleedLeftDraw,
  detailsColumnTopEdgeDraw,
  detailsColumnTopRightCornerDraw,
  detailsColumnColumnDividerDraw,
  formColumnBottomLeftCornerDraw,
  formColumnBottomEdgeDraw,
  formColumnBottomBleedRightDraw,
] = chainBorderDraw([
  { drawDurationMs: 300, drawDirection: 'ltr' },
  { drawDurationMs: 300, drawDirection: 'ltr' },
  { drawDurationMs: 450 },
  { drawDurationMs: 180 },
  { drawDurationMs: 450 },
  { drawDurationMs: 300, drawDirection: 'ltr' },
  { drawDurationMs: 300 },
])

/** Load-in draw timing keyed by border name. */
export const getInTouchOverlayBorderDrawAnimation = {
  [getInTouchOverlayBorderNames.detailsColumnTopBleedLeft]:
    detailsColumnTopBleedLeftDraw,
  [getInTouchOverlayBorderNames.detailsColumnTopEdge]: detailsColumnTopEdgeDraw,
  [getInTouchOverlayBorderNames.detailsColumnTopRightCorner]:
    detailsColumnTopRightCornerDraw,
  [getInTouchOverlayBorderNames.detailsColumnColumnDivider]:
    detailsColumnColumnDividerDraw,
  [getInTouchOverlayBorderNames.formColumnBottomEdge]: formColumnBottomEdgeDraw,
  [getInTouchOverlayBorderNames.formColumnBottomLeftCorner]:
    formColumnBottomLeftCornerDraw,
  [getInTouchOverlayBorderNames.formColumnBottomBleedRight]:
    formColumnBottomBleedRightDraw,
} as const
